import type { FC } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { cn } from "@/lib/utils";

/** Same-origin app link (relative, or absolute onto this deployment's host). */
function isOwnAppHref(href: string | undefined): boolean {
	if (!href) return false;
	try {
		const base =
			typeof window === "undefined" ? "https://localhost" : window.location.href;
		const url = new URL(href, base);
		if (url.protocol !== "https:" && url.protocol !== "http:") return false;
		return typeof window === "undefined" || url.host === window.location.host;
	} catch {
		return false;
	}
}

/**
 * Shared markdown renderer for agent/user message prose. GitHub-flavoured
 * (tables, strikethrough, task lists) via remark-gfm. This is the single place
 * message text is rendered so chat matches the rest of the app's styling.
 *
 * The text here is model-authored, so markup that reaches out to another host
 * is treated as an injection vector rather than formatting:
 *  - `img` renders alt text only. A `![px](https://evil/track)` in a reply
 *    would otherwise beacon the viewer's IP on render. Real attachments do not
 *    come through here — they have their own upload-backed chip.
 *  - external `a` hrefs render as plain label text; only same-origin app links
 *    stay clickable, so a model cannot forge a trusted-looking outbound link.
 */
const components: Components = {
	h1: ({ children }) => (
		<h1 className="mt-3 mb-1 font-semibold text-base">{children}</h1>
	),
	h2: ({ children }) => (
		<h2 className="mt-3 mb-1 font-semibold text-base">{children}</h2>
	),
	h3: ({ children }) => (
		<h3 className="mt-2 mb-1 font-semibold text-sm">{children}</h3>
	),
	h4: ({ children }) => (
		<h4 className="mt-2 mb-1 font-semibold text-sm">{children}</h4>
	),
	p: ({ children }) => <p className="leading-relaxed">{children}</p>,
	ul: ({ children }) => (
		<ul className="my-1 list-disc space-y-0.5 pl-5">{children}</ul>
	),
	ol: ({ children }) => (
		<ol className="my-1 list-decimal space-y-0.5 pl-5">{children}</ol>
	),
	li: ({ children }) => <li className="leading-relaxed">{children}</li>,
	strong: ({ children }) => (
		<strong className="font-semibold">{children}</strong>
	),
	em: ({ children }) => <em className="italic">{children}</em>,
	a: ({ href, children }) =>
		isOwnAppHref(href) ? (
			<a
				href={href}
				target="_blank"
				rel="noopener noreferrer"
				className="font-medium text-primary underline underline-offset-2"
			>
				{children}
			</a>
		) : (
			<>{children}</>
		),
	img: ({ alt }) => <>{alt}</>,
	code: ({ className, children }) => {
		const isBlock = /^language-/.test(className ?? "");
		if (isBlock) return <code className={className}>{children}</code>;
		return (
			<code className="rounded bg-foreground/10 px-1 py-0.5 text-[0.85em]">
				{children}
			</code>
		);
	},
	pre: ({ children }) => (
		<pre className="my-2 overflow-x-auto rounded-lg border bg-foreground/5 p-3 text-xs">
			{children}
		</pre>
	),
	blockquote: ({ children }) => (
		<blockquote className="border-border border-l-2 pl-3 text-muted-foreground italic">
			{children}
		</blockquote>
	),
	hr: () => <hr className="my-3 border-border" />,
	table: ({ children }) => (
		<div className="my-2 overflow-x-auto">
			<table className="w-full border-collapse text-xs">{children}</table>
		</div>
	),
	th: ({ children }) => (
		<th className="border border-border px-2 py-1 text-left font-semibold">
			{children}
		</th>
	),
	td: ({ children }) => (
		<td className="border border-border px-2 py-1">{children}</td>
	),
};

export const MarkdownText: FC<{ text: string; className?: string }> = ({
	text,
	className,
}) => {
	return (
		<div className={cn("space-y-2 break-words text-sm", className)}>
			<ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
				{text}
			</ReactMarkdown>
		</div>
	);
};

/**
 * Single-line variant for dense surfaces (notification/timeline card previews).
 * Same inline formatting as MarkdownText — bold, links, code — but paragraphs
 * collapse to inline runs so the result clamps like a text node. Block content
 * (lists, headings, quotes) is intentionally flattened to plain text: callers
 * pass an already-summarised single sentence, not a full document.
 */
const inlineComponents: Components = {
	...components,
	p: ({ children }) => <>{children}</>,
	h1: ({ children }) => <>{children}</>,
	h2: ({ children }) => <>{children}</>,
	h3: ({ children }) => <>{children}</>,
	h4: ({ children }) => <>{children}</>,
	h5: ({ children }) => <>{children}</>,
	h6: ({ children }) => <>{children}</>,
	ul: ({ children }) => <>{children}</>,
	ol: ({ children }) => <>{children}</>,
	li: ({ children }) => <>{children}</>,
	blockquote: ({ children }) => <>{children}</>,
	pre: ({ children }) => <>{children}</>,
	hr: () => null,
};

/**
 * Same inline renderer, links defanged: the label still shows, but no anchor is
 * emitted. For bodies whose author is not the product itself — an agent calling
 * `notifications.send({ body })` supplies that string verbatim, so a live
 * `[do not click](https://evil)` inside trusted-looking chrome is phishing.
 */
const inlineComponentsNoLinks: Components = {
	...inlineComponents,
	a: ({ children }) => <>{children}</>,
};

export const InlineMarkdownText: FC<{
	text: string;
	className?: string;
	/**
	 * "app-links" keeps anchors whose href resolves to this app (the shared `a`
	 * renderer already drops every external and non-http one) — for
	 * server-composed bodies whose entity permalinks should stay clickable.
	 * "strip" is the default and emits no anchors at all, for bodies an agent
	 * supplies verbatim, where even a same-origin link is unearned trust.
	 */
	linkPolicy?: "app-links" | "strip";
}> = ({ text, className, linkPolicy = "strip" }) => {
	return (
		<span className={cn("break-words", className)}>
			<ReactMarkdown
				remarkPlugins={[remarkGfm]}
				components={
					linkPolicy === "app-links" ? inlineComponents : inlineComponentsNoLinks
				}
			>
				{text}
			</ReactMarkdown>
		</span>
	);
};
